import { useState } from "react";
import { useFormik } from "formik";
import { Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import compInfos from "./add-new-server-form.json";
import { useLangContext } from "../../context/langContext";
import { number, object, string } from "yup";
import MuiTextField from "../../components/common/text-field/MuiTextField";
import { useContactsContext } from "../../context/contactsContext";
import { useProtectedAxiosInstance } from "../../hooks/axiosInstance";
import errorResolver from "../../utils/error-resolver";
import { useNotificationContext } from "../../context/notificationContext";
import { PbxServerSchema } from "../../services/Schemas/PbxServer";
import { PbxServerCreateRequest } from "../../services/Endpoints/pbxserver/PbxServerCreateEndpoint";
import { PbxServerUpdateRequest } from "../../services/Endpoints/pbxserver/PbxServerUpdateEndpoint";
import MuiSelect from "../../components/common/select/MuiSelect";
import { useCanAccess } from "../../hooks/canAccess";
import { AccessEnums } from "../../constants/accessEnum";

const successMsg = {
  fa: {
    create: "سرور ایجاد.",
    update: "سرور آپدیت شد.",
  },
  en: {
    create: "سرور ایجاد.",
    update: "سرور آپدیت شد.",
  },
};

export interface IAddNewServerFormProps {
  onClose: () => void;
  handleReload: () => void;
  serverInfo?: PbxServerSchema;
}

export default function AddNewServerForm({
  onClose,
  serverInfo,
  handleReload,
}: Readonly<IAddNewServerFormProps>) {
  const [waiting, setWaiting] = useState(false);
  useContactsContext();
  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();
  const { can } = useCanAccess();

  const { lang } = useLangContext();

  const infos = compInfos[lang];
  const loginSchema = object({
    workspace_id: string().optional(),
    pbx_driver: string().required("درایو pbx را وارد کنید ."),
    server_url: string().required("آدرس سرور را وارد کنید."),
    username: string().required("نام کاربری خود را وارد کنید ."),
    max_pending_calls: number().required(
      "حداکثر تماس های درحال  انتظار را وارد کنید .",
    ),
    password: string().required("رمز عبور مورد نظر را وارد کنید ."),
    caption: string().required("نام سرور را وارد کنید "),
    description: string().required("توضیحات سرور را وارد کنید ."),
    status: string().required("وضعیت سرور را مشخص کنید ."),
  });

  const serverStatus = {
    offline: "افلاین",
    online: "آنلاین",
    unreachable: "خارج از دسترس",
    Error: "خطا",
  };

  const formik = useFormik({
    initialValues: {
      workspace_id: serverInfo?.workspace_id ?? "",
      pbx_driver: serverInfo?.pbx_driver ?? "",
      server_url: serverInfo?.server_url ?? "",
      username: serverInfo?.username ?? "",
      max_pending_calls: serverInfo?.max_pending_calls ?? "",
      password: serverInfo?.password ?? "",
      caption: serverInfo?.caption ?? "",
      description: serverInfo?.description ?? "",
      status: serverInfo?.status ?? "online",
    },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      setWaiting(true);
      const serverCreateRequest = new PbxServerCreateRequest(protectedInstance);
      const serverUpdateRequest = new PbxServerUpdateRequest(protectedInstance);
      try {
        const {
          password,
          pbx_driver,
          server_url,
          username,
          workspace_id,
          caption,
          status,
          max_pending_calls,
          description,
        } = values;

         !serverInfo
          ? await serverCreateRequest.send({
              username,
              password,
              server_url,
              pbx_driver,
              workspace_id: workspace_id ? String(workspace_id) : null,
              caption,
              status,
              max_pending_calls: Number(max_pending_calls),
              description,
            })
          : await serverUpdateRequest.send(serverInfo.id, {
              username,
              password,
              server_url,
              status,
              pbx_driver,
              workspace_id: workspace_id ? String(workspace_id) : null,
              caption,
              max_pending_calls: Number(max_pending_calls),
              description,
            });
        setWaiting(false);
        handleReload();

        onClose();
        setNotification({
          type: "success",
          message: !!serverInfo
            ? successMsg[lang].update
            : successMsg[lang].create,
        });
      } catch (error: any) {
        errorResolver({ error, setNotification });
        setWaiting(false);
      }
    },
    enableReinitialize: true,
  });    
  return (
    <Stack
      maxWidth="100%"
      width="100%"
      gap="20.31px"
      mx="auto"
      component="form"
      onSubmit={formik.handleSubmit}
    >
      {infos.inputs.map(({ Example, label, name, placeholder }) => (
        <>
          {name == "status" ? (
            <MuiSelect
              label={label}
              name={name}
              placeholder={placeholder}
              value={formik.values[name]}
              handleChange={formik.handleChange}
              options={Object.keys(serverStatus).map((key) => ({
                value: key,
                label: serverStatus[key as "offline"],
              }))}
            />
          ) : (
            <MuiTextField
              key={name}
              width="100%"
              label={label}
              placeholder={placeholder}
              name={name}
              inputMode={name == "phone" ? "numeric" : "text"}
              type="text"
              value={formik.values[name as "username"]}
              handleChange={formik.handleChange}
              multiline={name == "description"}
              Example={Example}
              error={
                formik.touched[name as "username"] &&
                Boolean(formik.errors[name as "username"]) &&
                formik.errors[name as "username"]
              }
            />
          )}
        </>
      ))}
      <LoadingButton
        disabled={!can(AccessEnums.AdminPbxServersCreate)}
        variant="confirm"
        sx={{
          mt: "10px",
        }}
        type="submit"
        loading={waiting}
        // onClick={() => handleReload?.()}
      >
        {infos.buttonText}
      </LoadingButton>
    </Stack>
  );
}
